import * as ko from "knockout";
import SubscriptionViewModel from "../factories/subscription-view-model";
import TabPage from "../factories/tab-page";
import BaoService from "../services/bao-service";
import BaoDialog from "./../factories/bao-dialog";
import BaoRouter from "./../services/bao-router";
import BaoMenu from "./../services/bao-menu";

/**
 * Contains Frontend logic for the Display and Rendering of InlineFrameReports
 */
class InlineFrameReportRenderEngine extends SubscriptionViewModel {
    public constructor(tabPage: TabPage) {
        super(tabPage);
    }

    public AvailableInlineFrameReports: KnockoutObservableArray<any> = ko.observableArray<any>();
    public SelectedInlineFrameReport: KnockoutObservable<any> = ko.observable<any>();
    public IsFavorite: KnockoutObservable<boolean> = ko.observable(false);
    public SettingsKeyPermission: KnockoutObservable<string> = ko.observable("InlineFrameReportEdit");
    public SettingsKeyPermissionApplicationId: KnockoutObservable<string> = ko.observable(window.FinitoApplicationId);

    public SelectedInlineFrameReportURL: KnockoutObservable<string> = ko.computed(() => {
        if (this.SelectedInlineFrameReport() != null) {
            return this.SelectedInlineFrameReport().URL;
        }
        else {
            return "";
        }
    })

    /**
     * Returns the relative path of the inline frame report based on the SelectedInlineFrameReport variable
     */ 
    public InlineFrameReportRenderEngineDeepLink: KnockoutObservable<string> = ko.computed(() => {
        if (this.SelectedInlineFrameReport() != null) {
            return this.SelectedInlineFrameReport().InlineFrameReportId ? '#/InlineFrameReportRenderEngine/' + this.SelectedInlineFrameReport().InlineFrameReportId : null;
        }
        else {
            return "";
        }
    })

    /**
     * Returns the relative path of the base data mask for the inline frame report based on the SelectedInlineFrameReport variable
     */ 
    public SettingsUrl: KnockoutObservable<string> = ko.computed(() => {
        if (this.SelectedInlineFrameReport() != null) {
            return this.SelectedInlineFrameReport().InlineFrameReportId ? '#InlineFrameReport/' + this.SelectedInlineFrameReport().InlineFrameReportId : null;
        }
        else {
            return "";
        }
    })

    public init(): JQueryPromise<void> {
        let def = $.Deferred<void>();

        this.GetAvailableInlineFrameReports().then(() => {
            this.SetSelectedInlineFrameReportFromUrlData();
            def.resolve();
        });

        this.RegisterMenuClick();

        this.subscribe(this.SelectedInlineFrameReport, (value) => { this.OnSelectedInlineFrameReportChanged(value); })
        this.subscribe(this.TabPage.UrlData, () => { this.OnUrlDataChanged(); })

        return def.promise();
    }

    /**
    * Gets all available InlineFrameReports for the current user from the backend.
    * The loaded InlineFrameReports get stored in the AvailableInlineFrameReports variable. 
    */
    public GetAvailableInlineFrameReports() {
        return BaoService.post("InlineFrameReport/GetAvailableInlineFrameReports", (result) => {
            this.AvailableInlineFrameReports(result); 
        });
    }

    /**
     * Checks if the given inlineFrameReportId is in the AvailableInlineFrameReports array.
     * If its present it is set as the SelectedInlineFrameReport
     * If not a Error Message is displayed
     * @param inlineFrameReportId
     */
    public SetSelectedInlineFrameReport(inlineFrameReportId) {
        if (inlineFrameReportId != null) {
            let urlInlineFrameReport = this.AvailableInlineFrameReports().find(x => x.InlineFrameReportId == inlineFrameReportId);

            if (urlInlineFrameReport == null) {
                new BaoDialog(BaoService.Session.GetApplicationName(), BaoService.Session.GetMessage("InlineFrameReportNotAvailable"), "info");
            }
            else {
                this.SelectedInlineFrameReport(urlInlineFrameReport);
            }
        }
    } 

    /**
     * Gets the id from the URL
     * If there is a ID SetSelectedInlineFrameReport is called
     */
    public SetSelectedInlineFrameReportFromUrlData() {
        let urlInlineFrameReportId = BaoRouter.getRouteValue("id", this.TabPage);

        if (urlInlineFrameReportId != null && this.SelectedInlineFrameReport() == null) {
            this.SetSelectedInlineFrameReport(urlInlineFrameReportId);
        }
        if (urlInlineFrameReportId != null && urlInlineFrameReportId != this.SelectedInlineFrameReport().InlineFrameReportId) {
            this.SetSelectedInlineFrameReport(urlInlineFrameReportId);
        }
    }

    /**
     * Performs a Refresh
     */
    public Refresh() {
        let selectedInlineFrameId = this.SelectedInlineFrameReport().InlineFrameReportId;

        this.GetAvailableInlineFrameReports().then(() => {
            this.SetSelectedInlineFrameReport(selectedInlineFrameId);
        })
    }

    /**
     * Updates the favourite status of the inline frame report
     * Removes or adds the URL as QuickStartModule 
     */
    public ChangeFavoriteStatus() {
        let promise;
        if (this.IsFavorite()) {
            promise = this.RemoveAsFavorite();
        }
        else {
            promise = this.AddAsFavorite();
        }
        promise.then(() => { BaoService.raiseEvent('QuickStartModules:refresh', null); });
    }

    /**
     * Handels a change of the SelectedSummaryFilter variable
     * Sets the Title and ObjectId of the TabPage and checks if the selected inline frame report is a favorite.
     * @param newVal
     */
    protected OnSelectedInlineFrameReportChanged(newVal) {
        this.TabPage.Title(newVal.Title);   
        this.CheckIsQuickStartModuleActive();
    }

    /**
     * Handels a change of the id in the URL
     * Gets the new id from the URL and checks if its the currently set inline frame report or not
     * If not the selected inline frame report is changed to the new one.
     */
    protected OnUrlDataChanged() {
        this.SetSelectedInlineFrameReportFromUrlData();
    }

    protected AddAsFavorite() {
        this.TabPage.IsLoading(true);
        return BaoService.post("StartModule/AddQuickStartModule", JSON.stringify({
            caption1: this.SelectedInlineFrameReport().Title,
            caption2: this.SelectedInlineFrameReport().Title,
            caption3: this.SelectedInlineFrameReport().Title,
            caption4: this.SelectedInlineFrameReport().Title,
            caption5: this.SelectedInlineFrameReport().Title,
            webUrl: this.InlineFrameReportRenderEngineDeepLink(),
            color: "text-primary",
            icon: "fa fa-area-chart fa-5x",
            sort: 120
        })).done(result => {
            if (result) {
                this.IsFavorite(true);
            }
            this.TabPage.IsLoading(false);
        });
    }

    protected RemoveAsFavorite() {
        this.TabPage.IsLoading(true);
        return BaoService.post("StartModule/RemoveQuickStartModuleByWebUrl", JSON.stringify({ webUrl: this.InlineFrameReportRenderEngineDeepLink()})).done(result => {
            if (result) {
                this.IsFavorite(false);
            }
            this.TabPage.IsLoading(false);
        });
    }
    
    protected CheckIsQuickStartModuleActive () {
        return BaoService.post("StartModule/IsQuickStartModuleActive", JSON.stringify({ webUrl: this.InlineFrameReportRenderEngineDeepLink()})).done(result => {
            this.IsFavorite(result);
        });
    }

    protected RegisterMenuClick() {
        BaoMenu.registerEvent((btn) => {
            if (!(this.TabPage != null && this.TabPage.IsActiveTab != null && this.TabPage.IsActiveTab() === true))
                return;
            let actionKey = btn.ActionKey();
            switch (actionKey) {
                case "InlineFrameReportRenderEngineRefresh":
                    this.Refresh();
                    break;
            }
        });
    }
}
export default InlineFrameReportRenderEngine;